import { UntypedFormGroup } from '@angular/forms'
import {
  CheckboxAnswer,
  CheckboxItem,
  QuestionnaireField,
  QuestionnaireQuestion,
  QuestionnaireSection
} from '../models/questionnaire.model'

// The questionnaire sent by the backend has `section.questions` as a flat list
// (OneToMany on sectionId) AND each question still carries `children`, so a
// naive walk would emit children twice. Dedup by id covers both shapes.
export function flattenQuestions(
  questions: QuestionnaireQuestion[]
): QuestionnaireQuestion[] {
  const seen = new Set<number>()
  const result: QuestionnaireQuestion[] = []
  const visit = (q: QuestionnaireQuestion) => {
    if (seen.has(q.id)) return
    seen.add(q.id)
    result.push(q)
    q.children?.forEach(visit)
  }
  questions.forEach(visit)
  return result
}

export function buildAnswersPayload(
  currentSection: QuestionnaireSection | null,
  form: UntypedFormGroup | null,
  resolvedFields: QuestionnaireField[],
  refs: Record<number, { toolIds: number[]; metricIds: number[] }>
): Array<{ questionId: number; answer: string; answerMapped?: string; toolIds?: number[]; metricIds?: number[] }> {
  if (!currentSection || !form) return []

  const allQuestions = flattenQuestions(currentSection.questions)
  const answers: Array<{ questionId: number; answer: string; answerMapped?: string; toolIds?: number[]; metricIds?: number[] }> = []
  for (const question of allQuestions) {
    if (question.type === 'empty') {
      if (question.collectRefs) {
        const toolIds = refs[question.id]?.toolIds || []
        const metricIds = refs[question.id]?.metricIds || []
        if (toolIds.length || metricIds.length) {
          answers.push({
            questionId: question.id,
            answer: '',
            ...(toolIds.length ? { toolIds } : {}),
            ...(metricIds.length ? { metricIds } : {})
          })
        }
      }
      continue
    }

    const value = form.get(question.questionKey)?.value

    if (question.type === 'checkbox') {
      const selected = Array.isArray(value) ? value : []
      if (selected.length === 0) continue

      const field = resolvedFields.find(
        (f) => f.property === question.questionKey
      )
      const storedSpecify = field?.specifyText || {}
      const items = (question.items as CheckboxItem[]) || []

      const specifyText: Record<string, string> = {}
      for (const item of items) {
        if (
          item.hasSpecify &&
          selected.includes(item.value) &&
          storedSpecify[item.value]
        ) {
          specifyText[item.value] = storedSpecify[item.value]
        }
      }

      const checkboxAnswer: CheckboxAnswer = { selected }
      if (Object.keys(specifyText).length > 0) {
        checkboxAnswer.specifyText = specifyText
      }

      const cbToolIds = refs[question.id]?.toolIds || []
      const cbMetricIds = refs[question.id]?.metricIds || []
      answers.push({
        questionId: question.id,
        answer: JSON.stringify(checkboxAnswer),
        ...(cbToolIds.length ? { toolIds: cbToolIds } : {}),
        ...(cbMetricIds.length ? { metricIds: cbMetricIds } : {})
      })
    } else if (question.type === 'matrix') {
      if (value && value !== '') {
        answers.push({
          questionId: question.id,
          answer: value
        })
      }
    } else {
      const questionId = question.id
      const toolIds = refs[questionId]?.toolIds || []
      const metricIds = refs[questionId]?.metricIds || []

      if (question.type === 'text') {
        let answerMapped = value || ''
        answers.push({
          questionId,
          answer: value || '',
          ...(answerMapped !== value ? { answerMapped } : {}),
          ...(toolIds.length ? { toolIds } : {}),
          ...(metricIds.length ? { metricIds } : {})
        })
      } else if (value !== null && value !== undefined && value !== '') {
        let answerMapped = value
        if (question.type === 'radio' && question.items) {
          const item = (question.items as any[]).find(
            (i) => (i.value ?? i.label) === value
          )
          answerMapped = item?.label || value
        }
        const radioToolIds = question.type === 'radio' && value === 'Yes' ? (refs[questionId]?.toolIds || []) : []
        const radioMetricIds = question.type === 'radio' && value === 'Yes' ? (refs[questionId]?.metricIds || []) : []
        answers.push({
          questionId,
          answer: value,
          answerMapped: answerMapped !== value ? answerMapped : undefined,
          ...(radioToolIds.length ? { toolIds: radioToolIds } : {}),
          ...(radioMetricIds.length ? { metricIds: radioMetricIds } : {})
        })
      }
    }
  }
  return answers
}
